import { AlertTriangle as AlertTriangleDefault, ArrowRefresh2, Credits } from 'blaze-ui/icons'
import Styles from './GenerationFailedPreview.module.scss'
import { Text } from 'blaze-ui/Text/Text'
import { Button } from 'blaze-ui/Button'
import { useRegenerateBrandPlanContent } from 'hooks/queries/brandPlans/useRegenerateBrandPlanContent'
import { useCurrentBrandPlan } from 'hooks/queries/brandPlans/useCurrentBrandPlan'
import cx from 'classnames'
import { Heading } from 'blaze-ui/Heading/Heading'
import { AlertTriangle as AlertTriangleLarge } from 'blaze-ui/icons/40'
import { showErrorMessageToast } from 'common/MessageToasts/ErrorMessageToast/ErrorMessageToast'
import { useFlipperFlag } from 'hooks/useFlipperFlag'
import { useQueryClient } from 'react-query'
import { calendarKeys } from 'hooks/queries/calendar/calendarKeys'
import { type AlmanacFileCalendarItem } from 'hooks/queries/calendar/types'
import { useCreditUsage } from 'hooks/useCreditUsage'
import { useNeedsCreditsForCost } from 'hooks/useNeedsCreditsForCost'
import { useCategoryCreditCosts } from 'hooks/useCategoryCreditCosts'
import { usePurchaseContentCreditsModal } from 'hooks/usePurchaseContentCreditsModal'
import { getContentCategoryForScheduleContent } from 'utils/brandPlans/contentCategories'
import type { BrandPlanScheduleContent, PostContentType } from 'types'
import { observer } from 'mobx-react-lite'

type GenerationFailedPreviewProps = {
  fileId: string
  onClose?: () => void
  variant: 'eventCard' | 'fullScreenPreview'
  channel?: string | null
  contentType?: PostContentType | null
}

export const GenerationFailedPreview = observer(
  ({ fileId, variant, onClose, channel, contentType }: GenerationFailedPreviewProps) => {
    const { data: brandPlan, isLoading: isLoadingBrandPlan } = useCurrentBrandPlan()
    const calendarV2 = useFlipperFlag('calendar_v2')
    const queryClient = useQueryClient()

    const hasContentStrategies = !!brandPlan?.has_content_strategies

    const categoryCosts = useCategoryCreditCosts()
    const category = channel
      ? getContentCategoryForScheduleContent({
          type: channel,
          content_type: contentType,
        } as BrandPlanScheduleContent)
      : null
    const cost = category ? categoryCosts[category] : 0
    const { needsCredits: needsCreditsForCost, autopilotContentCreditsPrice } = useNeedsCreditsForCost(cost)
    const { isOutOfCredits, canAutoReload } = useCreditUsage()

    const needsCredits = hasContentStrategies ? needsCreditsForCost : isOutOfCredits && !canAutoReload

    const openPurchaseModal = usePurchaseContentCreditsModal()

    const { mutateAsync: regenerateContent, isLoading: isLoadingRegenerateContent } = useRegenerateBrandPlanContent({
      onSuccess: () => {
        if (calendarV2) return

        const queries = queryClient.getQueriesData<AlmanacFileCalendarItem[]>(calendarKeys.all)
        queries.forEach(([queryKey, existingData]) => {
          if (!Array.isArray(existingData)) return

          const newData = existingData.filter((item) => item.key !== fileId)
          if (newData.length !== existingData.length) {
            queryClient.setQueryData(queryKey, newData)
          }
        })
      },
    })

    const handleRetry = async () => {
      if (!brandPlan) return

      try {
        await regenerateContent({
          brandPlanId: brandPlan.id,
          body: { file_ids: [fileId], initial_media_file_ids: [], content_scope: 'user_selected_content' },
        })
        onClose?.()
      } catch (error) {
        showErrorMessageToast(error instanceof Error ? error.message : 'Unable to retry generation. Please try again.')
      }
    }

    const handlePurchaseCredits = () => {
      if (!autopilotContentCreditsPrice) return
      openPurchaseModal({
        price: autopilotContentCreditsPrice,
        onSuccess: () => {
          void handleRetry()
        },
      })
    }

    const showInsufficientCreditsVariant = needsCredits && !!autopilotContentCreditsPrice
    const onAction = showInsufficientCreditsVariant ? handlePurchaseCredits : handleRetry
    const title = showInsufficientCreditsVariant ? 'Insufficient Credits' : 'Generation Failed'
    const ctaLabel = showInsufficientCreditsVariant ? 'Buy Credits' : 'Retry'
    const CtaIcon = showInsufficientCreditsVariant ? Credits : ArrowRefresh2

    const disableAction = showInsufficientCreditsVariant ? false : isLoadingBrandPlan || isLoadingRegenerateContent

    return variant === 'eventCard' ? (
      <EventCardPreview
        title={title}
        ctaLabel={ctaLabel}
        CtaIcon={CtaIcon}
        onAction={onAction}
        disableAction={disableAction}
      />
    ) : (
      <FullScreenPreview
        title={title}
        ctaLabel={ctaLabel}
        CtaIcon={CtaIcon}
        onAction={onAction}
        disableAction={disableAction}
      />
    )
  },
)

type PreviewProps = {
  title: string
  ctaLabel: string
  CtaIcon: typeof ArrowRefresh2
  onAction: () => void
  disableAction: boolean
}

const EventCardPreview = ({ title, ctaLabel, CtaIcon, onAction, disableAction }: PreviewProps) => {
  return (
    <div className={cx(Styles.root, Styles.eventCard)}>
      <div className={Styles.container}>
        <div className={Styles.description}>
          <AlertTriangleDefault size={24} color="var(--dark-80)" />
          <Text variant="secondary" fontWeight={'500'} color="var(--dark-80)">
            {title}
          </Text>
        </div>
        <ActionButton ctaLabel={ctaLabel} CtaIcon={CtaIcon} onAction={onAction} disableAction={disableAction} />
      </div>
    </div>
  )
}

const FullScreenPreview = ({ title, ctaLabel, CtaIcon, onAction, disableAction }: PreviewProps) => {
  return (
    <div className={cx(Styles.root, Styles.fullScreenPreview)}>
      <div className={Styles.fullScreenDescription}>
        <AlertTriangleLarge size={40} color="var(--dark-80)" />
        <Heading level={5} color="var(--dark-80)">
          {title}
        </Heading>
      </div>
      <ActionButton ctaLabel={ctaLabel} CtaIcon={CtaIcon} onAction={onAction} disableAction={disableAction} />
    </div>
  )
}

const ActionButton = ({
  ctaLabel,
  CtaIcon,
  onAction,
  disableAction,
}: Pick<PreviewProps, 'ctaLabel' | 'CtaIcon' | 'onAction' | 'disableAction'>) => {
  return (
    <Button className={Styles.retryButton} size="sm" onPress={onAction} frontIcon={CtaIcon} isDisabled={disableAction}>
      {ctaLabel}
    </Button>
  )
}
